import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {BaseItemObject, InitItemOpts} from '@PosterWhiteboard/items/item/item.types';
import {doesVideoHaveAudio, loadVideo} from '@Utils/video.util';
import {SyncToPageClock} from '@PosterWhiteboard/classes/sync-to-page-clock.class';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemEffectsObject} from '@PosterWhiteboard/classes/item-effects.class';
import {ItemEffects} from '@PosterWhiteboard/classes/item-effects.class';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import {loadImageAsync} from '@Utils/image.util';
import type {RGBA} from '@Utils/color.util';
import {colorToRgbaArray} from '@Utils/color.util';
import {FabricImage} from '@postermywall/fabricjs-2';
import {roundPrecision} from '@Utils/math.util';
import type {DeepPartial} from '@/global';

export interface MotionItemObject extends BaseItemObject {
  hashedFilename: string;
  duration: number;
  hasTransparency: boolean;
  frameRate: number;
  startTime: number;
  effects: ItemEffectsObject;
  endTime: number;
  volume: number;
  speed: number;
}

/**
 * The version at which we enabled uniform stroke for motion items.
 */
const VERSION_2 = 2;
const PLAY_INTERRUPTED_BY_PAUSE_ERROR_MESSAGE = 'The play() request was interrupted by a call to pause()';

const GREEN_SCREEN_COLOR_RGBA: RGBA = [42, 250, 6, 1];
export const DEFAULT_VIDEO_EXTENSION = 'webm';

export abstract class MotionItem extends Item {
  declare _fabricObject: FabricImage;
  public hashedFilename = '';
  public duration = 0;
  public hasTransparency = false;
  public frameRate = 0;
  public startTime = 0;
  public effects: ItemEffects;
  public endTime = 0;
  public volume = 1;
  public speed = 1;
  public override version = 2;
  public hasAudio: null | boolean = null;
  public htmlElement!: HTMLVideoElement;
  public syncToPageClock: SyncToPageClock;
  protected currentLoadedImageFrameSrc?: string;
  /**
   * Sync to poster click function is cached in this variable so that it can be unmounted on item remove. Inline use of that
   * function doesn't work because of bind (https://stackoverflow.com/questions/28800850/jquery-off-is-not-unbinding-events-when-using-bind)
   */
  private readonly syncToPageClockFunction;

  public constructor(page: Page) {
    super(page);
    // @ts-expect-error gota look into this ts error
    this.effects = new ItemEffects(this);
    this.syncToPageClock = new SyncToPageClock({
      getDuration: this.getStreamDuration.bind(this),
      getCurrentTime: this.getCurrentTime.bind(this),
      getPageDuration: (): number => this.page.getDuration(),
      seek: this.seek.bind(this),
      pauseAtEnd: this.shouldPauseAtStreamEnd(),
      stopFunction: this.stopItemStream.bind(this),
    });
    this.syncToPageClockFunction = this.syncToPageClock.syncToPage.bind(this.syncToPageClock);
  }

  override get fabricObject(): FabricImage {
    return this._fabricObject;
  }

  public shouldPauseAtStreamEnd(): boolean {
    return false;
  }

  public hasUniformStroke(): boolean {
    return this.version >= VERSION_2;
  }

  public override onRemove(): void {
    super.onRemove();
    this.page.off('time:updated', this.syncToPageClockFunction);
    this.htmlElement.pause();
  }

  public override getDuration(): number {
    const baseDuration = this.endTime - this.startTime;
    return baseDuration / this.speed;
  }

  public getStreamDuration(): number {
    return this.getDuration();
  }

  public override toObject(): MotionItemObject {
    return {
      ...super.toObject(),
      effects: this.effects.toObject(),
      hashedFilename: this.hashedFilename,
      duration: this.duration,
      hasTransparency: this.hasTransparency,
      frameRate: this.frameRate,
      startTime: this.startTime,
      endTime: this.endTime,
      volume: this.volume,
      speed: this.speed,
    };
  }

  public async stopItemStream(): Promise<void> {
    if (this.htmlElement && roundPrecision(this.htmlElement.currentTime, 1) !== roundPrecision(this.startTime, 1)) {
      await this.stop();
    }
  }

  public override async stop(): Promise<void> {
    await this.pause();
    this.htmlElement.currentTime = this.startTime;
  }

  public override getColors(): RGBA[] {
    const colors: RGBA[] = super.getColors();

    if (this.fabricObject.shadow?.isGlow()) {
      colors.push(colorToRgbaArray(this.fabricObject.shadow.color));
    }

    if (this.effects.tint) {
      colors.push(this.effects.tintColor);
    }

    if (this.effects.multiply) {
      colors.push(this.effects.multiplyColor);
    }

    return colors;
  }

  public override isStreamingMediaItem(): boolean {
    return true;
  }

  public override copyVals(obj: DeepPartial<ImageItemObject>): void {
    const {effects, ...itemObj} = obj;
    super.copyVals(itemObj);
    this.effects.copyVals(effects);
  }

  /**
   * Hook for subclasses to inject logic *after* base Item logic
   * but *before* MotionItem's own effects are applied.
   */
  protected applyPreEffectsLogic(): void {
    // Base implementation does nothing
  }

  public override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.applyPreEffectsLogic();
    this.updateRemoveColorFilter();
    this.effects.applyItemEffects();
    await this.effects.applyBorderEffects();
    this.htmlElement.muted = !this.hasAudio || this.volume === 0;
  }

  public override async pause(): Promise<void> {
    await super.pause();
    return new Promise((resolve) => {
      this.htmlElement.pause();
      resolve();
    });
  }

  protected override async fixChanges(applyVersionFixesData: Record<string, any>): Promise<void> {
    await super.fixChanges(applyVersionFixesData);
    this.applyFixForVersion2();
  }

  private applyFixForVersion2(): void {
    if (!this.hasUniformStroke()) {
      this.border.solidBorderThickness = Math.round(this.border.solidBorderThickness * this.fabricObject.scaleX);
      this.version = VERSION_2;
    }
  }

  public override async onItemAddedToPage(): Promise<void> {
    this.page.on('time:updated', this.syncToPageClockFunction);
    await this.seekToPageTime();

    if (this.page.isPlaying) {
      await this.play();
    }
  }

  public getCurrentTime(): number {
    const timeWithinVideoSegment = this.htmlElement.currentTime - this.startTime;
    return timeWithinVideoSegment / this.speed;
  }

  public async seekToPageTime(): Promise<void> {
    await this.seek(this.page.clock.getCurrentTime());
    this.page.fabricCanvas.requestRenderAll();
  }

  public override seek(time: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.htmlElement.addEventListener(
        'seeked',
        () => {
          this.page.fabricCanvas.requestRenderAll();
          resolve();
        },
        {
          capture: false,
          once: true,
        }
      );

      const timeWithinVideoSegment = time * this.speed;
      this.htmlElement.currentTime = timeWithinVideoSegment + this.startTime;
    });
  }

  public override async play(): Promise<void> {
    if (!this.htmlElement) {
      throw new Error('Video element is not defined');
    }

    try {
      const promise = this.htmlElement.play();

      if (typeof promise !== 'undefined') {
        await promise;
      }
    } catch (e) {
      if (!(e instanceof Error && e.message.includes(PLAY_INTERRUPTED_BY_PAUSE_ERROR_MESSAGE))) {
        throw e;
      }
    }
  }

  public getFrameTimeForPageTime(pageTime: number): number {
    return (pageTime > this.getDuration() ? pageTime % this.getDuration() : pageTime) * this.speed + this.startTime + 0.001;
  }

  public async setImageFrame(repoTempFilePath: string): Promise<void> {
    const src = `${window.PMW.BASE_URL}repo/temp/${repoTempFilePath}`;
    if (this.currentLoadedImageFrameSrc === src) {
      return;
    }

    const image = await loadImageAsync(src);
    const sacledWidth = this.fabricObject.scaleX * this.fabricObject.width;
    const scaledHeight = this.fabricObject.scaleY * this.fabricObject.height;

    this.fabricObject.setElement(image);
    this.fabricObject.set({
      scaleX: sacledWidth / image.width,
      scaleY: scaledHeight / image.height,
    });
  }

  protected async getFabricObjectForItem(opts?: InitItemOpts): Promise<FabricImage> {
    await this.loadVideoElement();
    return new FabricImage(this.htmlElement, {
      ...super.getCommonOptions(),
      ...this.getScalesAndShadowToMaintainScaledDimensions(this.htmlElement.width, this.htmlElement.height, opts),
      strokeUniform: true,
    });
  }

  protected async loadVideoElement(): Promise<void> {
    this.htmlElement = await loadVideo(this.getVideoUrl(), {videoElementAttributes: this.getVideoElementAttributes()});
    this.hasAudio = doesVideoHaveAudio(this.htmlElement);
    this.htmlElement.loop = !this.shouldPauseAtStreamEnd();
    this.htmlElement.muted = !this.hasAudio || this.volume === 0;
    this.htmlElement.playbackRate = this.speed;
    this.duration = this.htmlElement.duration;
    this.endTime = this.endTime === 0 ? this.htmlElement.duration : this.endTime;

    this.htmlElement.width = this.htmlElement.videoWidth;
    this.htmlElement.height = this.htmlElement.videoHeight;
  }

  protected getVideoElementAttributes(): Record<string, any> {
    return {};
  }

  protected abstract getVideoUrl(): string;

  protected isGreenScreenMp4Loaded(): boolean {
    return false;
  }

  private updateRemoveColorFilter(): void {
    if (this.isGreenScreenMp4Loaded()) {
      this.effects.removeColor = true;
      this.effects.removeColorValue = GREEN_SCREEN_COLOR_RGBA;
    } else {
      this.effects.removeColor = false;
    }
  }
}
